import { useState, useEffect, useContext } from 'react';
import { Box, Stack, Paper, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import BookingHeader from './bookingHeader';
import ConfirmDialog from '../shared/components/confirmDialog';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseServiceBookingSearchAPIUrl, NurseServiceBookingAPIUrl, GetDayJSValueFromUTCDate } from '../utils/extensions/functionEx';
import { DATE_DISPLAY_FORMAT } from '../startups/apiConfig';
import PageLoading from '../shared/components/backdrop';
import BookingMenuPopup from './bookingMenuPopup';
import { useCookies } from 'react-cookie';

const ServiceBookingSearch = (props) => {

    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, SetBookingSearchText, homeState, ClearLoggedUser } = useContext(HomeContext);

    const [dbNurseBookings, setDbNurseBookings] = useState([]);
    const [isOpenPageLoad, setIsOpenPageLoad] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedNurseBookingId, setSelectedBookingId] = useState(0);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        async function fetchNurseBookings() {
            searchNurseBookings(homeState.bookingSearchText.trim().length > 0 ? homeState.bookingSearchText.trim() : '');
        }
        fetchNurseBookings();
    }, []);

    const searchNurseBookings = async (searchText) => {
        try {
            setIsOpenPageLoad(true);
            let tempNurseBookings = [];
            SetBookingSearchText(searchText);

            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseServiceBookingSearchAPIUrl(homeState.loggedUser.companyCode, searchText), {
                method: "GET",
                headers: headers
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                tempNurseBookings = await response.json();
            } else {
                ShowAPIError(response)
            }
            setDbNurseBookings([...tempNurseBookings]);
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            setIsOpenPageLoad(false);
        }
    }

    const handleSearchClick = async (searchText) => {
        await searchNurseBookings(searchText.trim());
    }

    const handlePageLoadingClose = () => {
        setIsOpenPageLoad(false);
    }

    const handleDeleteClick = (bookingId) => {
        setIsDeleteConfirmOpen(true);
        setSelectedBookingId(bookingId);
    }

    const handleDeleteCancelClick = () => {
        setIsDeleteConfirmOpen(false);
        setSelectedBookingId(0);
    }

    const handleDeleteOKClick = async () => {
        try {
            setIsOpenPageLoad(true);
            setIsDeleteConfirmOpen(false);

            const tempSelectedBooking = dbNurseBookings.find(booking => booking.id === selectedNurseBookingId);

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseServiceBookingAPIUrl(homeState.loggedUser.companyCode), {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify({ 'id': selectedNurseBookingId, 'address_id': tempSelectedBooking.address_id })
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Service booking successfully deleted.');
                await searchNurseBookings(homeState.bookingSearchText.length > 0 ? homeState.bookingSearchText : '');
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            setIsOpenPageLoad(false);
            setSelectedBookingId(0);
        }
    }


    return (
        <Box sx={{ bgcolor: '#cfe8fc', padding: '8px, 0' }}>
            <Stack spacing={0}>
                {
                    isOpenPageLoad &&
                    <PageLoading isOpenPageLoading={isOpenPageLoad} handleClose={handlePageLoadingClose} />
                }
                {
                    isDeleteConfirmOpen &&
                    <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Delete"
                        message="Are you sure you want to delete the service booking?"
                        handleClickOK={handleDeleteOKClick}
                        handleClickCancel={handleDeleteCancelClick} />

                }
                <BookingHeader handleSearchClick={handleSearchClick} />
                <TableContainer component={Paper} sx={{ maxHeight: '79vh', borderRadius: '0' }}>
                    <Table stickyHeader sx={{ minWidth: 700, width: '1980px', overflowX: 'auto' }}>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell align="center" sx={{ width: '55px' }}></StyledTableCell>
                                <StyledTableCell sx={{ width: '150px' }}>Booking Date</StyledTableCell>
                                <StyledTableCell sx={{ width: '200px' }}>Duty</StyledTableCell>
                                <StyledTableCell sx={{ width: '125px' }}>Duty Days</StyledTableCell>
                                <StyledTableCell sx={{ width: '150px' }}>Booked Date</StyledTableCell>
                                <StyledTableCell>Patient Name</StyledTableCell>
                                <StyledTableCell sx={{ width: '250px' }}>Place</StyledTableCell>
                                <StyledTableCell sx={{ width: '150px' }}>Phone No</StyledTableCell>
                                <StyledTableCell sx={{ width: '300px' }}>Nurse Name</StyledTableCell>
                                <StyledTableCell sx={{ width: '300px' }}>Nurse Phone No</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dbNurseBookings.map((row) => (
                                <StyledTableRow key={row.id.toString()}>
                                    <StyledTableCell align="center">
                                        <BookingMenuPopup bookingRow={row} handleDeleteClick={handleDeleteClick} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {GetDayJSValueFromUTCDate(row.booking_date).format(DATE_DISPLAY_FORMAT)}
                                    </StyledTableCell>
                                    <StyledTableCell>{row.service_type_name}</StyledTableCell>
                                    <StyledTableCell>{row.duty_days}</StyledTableCell>
                                    <StyledTableCell>{GetDayJSValueFromUTCDate(row.booked_date).format(DATE_DISPLAY_FORMAT)}</StyledTableCell>
                                    <StyledTableCell>{row.patient_name}</StyledTableCell>
                                    <StyledTableCell>{row.place}</StyledTableCell>
                                    <StyledTableCell>{row.phone_no}</StyledTableCell>
                                    <StyledTableCell>{row.nurse_name}</StyledTableCell>
                                    <StyledTableCell>{row.nurse_phone}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        </Box>
    );
}
export default ServiceBookingSearch;